/**
 * Techniker Störungen
 * PR-5B: TechLayout Integration
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TechLayout } from '../../components/TechLayout';
import { ModuleBadge } from '../../components/ModuleBadge';
import {
  AlertTriangle,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { formatBerlinDateDE } from '../../utils/berlinDate';

interface Disruption {
  id: string;
  shiftDate: string;
  shiftType: string;
  techStatus: string;
  description: string | null;
  machine: { name: string } | null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Offen', color: 'bg-red-100 text-red-800' },
  IN_PROGRESS: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  WAITING_PARTS: { label: 'Warte auf Teile', color: 'bg-orange-100 text-orange-800' },
  RESOLVED_CONFIRMED: { label: 'Bestätigt', color: 'bg-green-100 text-green-800' },
};

export default function TechDisruptionsPage() {
  const [disruptions, setDisruptions] = useState<Disruption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/tech/disruptions', { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(setDisruptions)
      .finally(() => setLoading(false));
  }, []);

  // RBAC wird von TechLayout gehandhabt

  const openDisruptions = disruptions.filter(d => d.techStatus === 'OPEN' || d.techStatus === 'IN_PROGRESS');

  return (
    <TechLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Störungen
              <ModuleBadge module="disruptions" />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {openDisruptions.length} offene Störungen
            </p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : disruptions.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Keine Störungen vorhanden</div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Maschine</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Beschreibung</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {disruptions.map(d => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">
                      {formatBerlinDateDE(d.shiftDate).slice(0, 6)}
                    </td>
                    <td className="px-4 py-3">{d.machine?.name || '–'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${STATUS_CONFIG[d.techStatus]?.color || 'bg-slate-100'}`}>
                        {STATUS_CONFIG[d.techStatus]?.label || d.techStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">
                      {d.description || '–'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/tech/disruptions/${d.id}`}
                        className="p-1 text-slate-400 hover:text-slate-600"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </TechLayout>
  );
}
